import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, Play, FileText, Download, ChevronDown, ChevronUp, Pencil, Check, X } from 'lucide-react';
import axios from 'axios';
import {
  actorLandscapeService,
  countryZone,
  RUNNING_STATUSES,
  type ActorLandscape,
  type ActorLandscapeSummary,
  type Actor,
  type ActorUpdate,
  type LandscapeStatus,
  type SizeCategory,
} from '../services/actorLandscapeService';
import { knowledgeBaseService, type KnowledgeBase } from '../services/knowledgeBaseService';
import PageHeader from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// ── Markdown → HTML (for the blob-URL report window) ─────────────────────────

function mdToHtml(md: string): string {
  const esc = (s: string) => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const inline = (s: string) => s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');

  const lines = md.split('\n');
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Headings
    const h = line.match(/^(#{1,4})\s+(.*)/);
    if (h) { const lv = h[1].length; out.push(`<h${lv}>${inline(esc(h[2]))}</h${lv}>`); i++; continue; }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) { out.push('<hr>'); i++; continue; }

    // Table — collect all consecutive pipe-lines
    if (line.trim().startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]); i++;
      }
      // row 0 = headers, row 1 = separator (--|--), rest = data
      const rows = tableLines
        .filter(l => !/^\s*\|[-| :]+\|\s*$/.test(l))  // drop separator row
        .map(l => l.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim()));
      if (rows.length > 0) {
        let t = '<table><thead><tr>';
        rows[0].forEach(h => { t += `<th>${inline(esc(h))}</th>`; });
        t += '</tr></thead><tbody>';
        rows.slice(1).forEach(r => {
          t += '<tr>'; r.forEach(c => { t += `<td>${inline(esc(c))}</td>`; }); t += '</tr>';
        });
        t += '</tbody></table>';
        out.push(t);
      }
      continue;
    }

    // Unordered list — collect consecutive items
    if (/^[\-\*] /.test(line)) {
      out.push('<ul>');
      while (i < lines.length && /^[\-\*] /.test(lines[i])) {
        out.push(`<li>${inline(esc(lines[i].replace(/^[\-\*] /, '')))}</li>`); i++;
      }
      out.push('</ul>');
      continue;
    }

    // Ordered list
    if (/^\d+\. /.test(line)) {
      out.push('<ol>');
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        out.push(`<li>${inline(esc(lines[i].replace(/^\d+\. /, '')))}</li>`); i++;
      }
      out.push('</ol>');
      continue;
    }

    // Blank line
    if (line.trim() === '') { i++; continue; }

    // Paragraph
    out.push(`<p>${inline(esc(line))}</p>`);
    i++;
  }
  return out.join('\n');
}

function openReportWindow(name: string, markdown: string) {
  // Open a blank tab first (same-origin), then write HTML directly into it.
  // This avoids blob-URL restrictions imposed by COOP/COEP headers.
  const win = window.open('', '_blank');
  if (!win) return;   // popup blocked — user will need to allow popups
  const body = mdToHtml(markdown);
  const safe = (s: string) => s.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  win.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>${safe(name)} — Report</title>
<style>
  body{font-family:system-ui,-apple-system,sans-serif;max-width:960px;margin:0 auto;padding:40px 32px;color:#334155;line-height:1.6}
  h1{color:#0f172a;font-size:1.4rem;margin-bottom:24px}
  h2{color:#1e293b;font-size:1.05rem;font-weight:700;margin-top:32px;margin-bottom:8px;padding-bottom:4px;border-bottom:1px solid #e2e8f0}
  h3{color:#334155;font-size:.95rem;font-weight:600;margin-top:20px;margin-bottom:6px}
  h4{color:#475569;font-size:.9rem;font-weight:600;margin-top:16px;margin-bottom:4px}
  table{width:100%;border-collapse:collapse;font-size:13px;margin:12px 0 20px}
  th{background:#f1f5f9;border:1px solid #cbd5e1;padding:8px 12px;text-align:left;font-weight:600;color:#475569}
  td{border:1px solid #e2e8f0;padding:7px 12px;vertical-align:top}
  tr:nth-child(even) td{background:#f8fafc}
  ul,ol{padding-left:24px;margin:6px 0 12px}li{margin:3px 0}
  strong{font-weight:600;color:#1e293b}em{color:#475569}
  p{margin:4px 0 10px}hr{border:none;border-top:1px solid #e2e8f0;margin:24px 0}
</style></head><body><h1>${safe(name)}</h1>${body}</body></html>`);
  win.document.close();
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_ORDER: LandscapeStatus[] = ['draft', 'extracted', 'normalizing', 'normalized', 'tech_mapped', 'generating', 'finalized'];

const ACTOR_TYPE_LABELS: Record<string, string> = {
  company: 'Company', university: 'University', research_lab: 'Research Lab',
  individual: 'Individual', government: 'Government', other: 'Other',
};

const SIZE_LABELS: Record<SizeCategory, string> = {
  large: 'Large (>10k)', intermediate: 'Intermediate (0.5–10k)', sme: 'SME (<500)', unknown: 'Unknown',
};

const COUNTRY_ZONE_LABELS: Record<string, string> = {
  FR: 'France', EU_UK: 'EU + UK', other: 'Non-EU/UK', unknown: 'Unknown country',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: LandscapeStatus }) => {
  const colors: Record<LandscapeStatus, string> = {
    draft: 'bg-slate-100 text-slate-600',
    extracted: 'bg-blue-100 text-blue-700',
    normalizing: 'bg-yellow-100 text-yellow-700',
    normalized: 'bg-purple-100 text-purple-700',
    tech_mapped: 'bg-amber-100 text-amber-700',
    generating: 'bg-orange-100 text-orange-700',
    finalized: 'bg-green-100 text-green-700',
  };
  const labels: Record<LandscapeStatus, string> = {
    draft: 'Draft', extracted: 'Extracted', normalizing: 'Normalizing…',
    normalized: 'Normalized', tech_mapped: 'Tech Mapped', generating: 'Generating…', finalized: 'Finalized',
  };
  return <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${colors[status]}`}>{labels[status]}</span>;
};

interface ActorRowProps {
  actor: Actor;
  onSave: (actorId: string, updates: ActorUpdate) => Promise<void>;
}

const ActorRow: React.FC<ActorRowProps> = ({ actor, onSave }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ActorUpdate>({});
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft({
      canonical_name: actor.canonical_name,
      actor_type: actor.actor_type,
      country: actor.country ?? '',
      parent_group: actor.parent_group ?? '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(actor.actor_id, { ...draft, country: draft.country || null, parent_group: draft.parent_group || null });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TableRow className="hover:bg-slate-50 cursor-pointer" onClick={() => !editing && setExpanded(e => !e)}>
        <TableCell className="py-1.5 pr-1 w-4">
          {expanded ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
        </TableCell>
        <TableCell className="py-1.5 font-medium text-sm max-w-[200px]">
          {editing ? (
            <Input className="h-6 text-xs" value={draft.canonical_name || ''} onChange={e => setDraft(d => ({ ...d, canonical_name: e.target.value }))} onClick={e => e.stopPropagation()} />
          ) : actor.canonical_name}
        </TableCell>
        <TableCell className="py-1.5">
          {editing ? (
            <Select value={draft.actor_type || actor.actor_type} onValueChange={v => setDraft(d => ({ ...d, actor_type: v as typeof actor.actor_type }))} >
              <SelectTrigger className="h-6 text-xs w-32" onClick={e => e.stopPropagation()}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ACTOR_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          ) : <span className="text-xs text-slate-600">{ACTOR_TYPE_LABELS[actor.actor_type] ?? actor.actor_type}</span>}
        </TableCell>
        <TableCell className="py-1.5">
          {editing ? (
            <Input className="h-6 text-xs w-16" maxLength={2} value={draft.country || ''} onChange={e => setDraft(d => ({ ...d, country: e.target.value.toUpperCase() }))} onClick={e => e.stopPropagation()} />
          ) : <span className="text-xs font-mono text-slate-500">{actor.country ?? '–'}</span>}
        </TableCell>
        <TableCell className="py-1.5">
          {actor.size_category && actor.size_category !== 'unknown'
            ? <span className="text-xs text-slate-600">{SIZE_LABELS[actor.size_category]}</span>
            : <span className="text-xs text-slate-300">–</span>}
        </TableCell>
        <TableCell className="py-1.5">
          {actor.actor_type === 'company' && actor.country
            ? <span className={`text-xs font-medium ${countryZone(actor.country) === 'FR' ? 'text-blue-700' : countryZone(actor.country) === 'EU_UK' ? 'text-indigo-600' : 'text-slate-500'}`}>
                {COUNTRY_ZONE_LABELS[countryZone(actor.country)]}
              </span>
            : <span className="text-xs text-slate-300">–</span>}
        </TableCell>
        <TableCell className="py-1.5">
          {editing ? (
            <Input className="h-6 text-xs" value={draft.parent_group || ''} onChange={e => setDraft(d => ({ ...d, parent_group: e.target.value }))} onClick={e => e.stopPropagation()} />
          ) : <span className="text-xs text-slate-500 truncate max-w-[150px] block">{actor.parent_group ?? '–'}</span>}
        </TableCell>
        <TableCell className="py-1.5 text-center text-xs text-slate-600">{actor.patent_count}</TableCell>
        <TableCell className="py-1.5 text-center text-xs text-slate-600">{actor.publication_count}</TableCell>
        <TableCell className="py-1.5 text-xs text-slate-500 max-w-[200px]">
          <div className="flex flex-wrap gap-1">
            {actor.technology_areas.slice(0, 3).map((t, i) => (
              <span key={i} className="inline-block bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 text-[10px]">{t}</span>
            ))}
          </div>
        </TableCell>
        <TableCell className="py-1.5 text-center">
          <span className="text-[10px] text-slate-400">{Math.round(actor.confidence * 100)}%</span>
        </TableCell>
        <TableCell className="py-1.5 text-right" onClick={e => e.stopPropagation()}>
          {editing ? (
            <div className="flex gap-1 justify-end">
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={saving} onClick={handleSave}><Check size={12} /></Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditing(false)}><X size={12} /></Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100" onClick={startEdit}><Pencil size={12} /></Button>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-slate-50 border-0">
          <TableCell colSpan={10} className="py-2 px-8 border-0">
            <div className="text-xs text-slate-500 space-y-1">
              {actor.aliases.length > 1 && (
                <p><span className="font-medium text-slate-600">Aliases:</span> {actor.aliases.join(' · ')}</p>
              )}
              <p><span className="font-medium text-slate-600">Roles:</span> {actor.roles.join(', ')}</p>
              <p><span className="font-medium text-slate-600">Documents:</span> {actor.document_ids.length}</p>
              {actor.technology_areas.length > 3 && (
                <p><span className="font-medium text-slate-600">All tech areas:</span> {actor.technology_areas.join(' · ')}</p>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ActorLandscapePage() {
  const { kbId } = useParams<{ kbId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [kb, setKb] = useState<KnowledgeBase | null>(null);
  const [summaries, setSummaries] = useState<ActorLandscapeSummary[]>([]);
  const [landscape, setLandscape] = useState<ActorLandscape | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null); // which step is running
  const pollingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [actorSearch, setActorSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sizeFilter, setSizeFilter] = useState('all');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [exporting, setExporting] = useState(false);

  useEffect(() => { if (kbId) load(); }, [kbId]);

  // If the landscape is in a running state (e.g., after page reload), resume polling
  useEffect(() => {
    if (landscape && RUNNING_STATUSES.includes(landscape.status) && !pollingRef.current) {
      setBusy(landscape.status === 'normalizing' ? 'normalize' : 'generate-report');
      pollUntilDone(landscape.id);
    }
  }, [landscape?.id, landscape?.status]);

  const pollUntilDone = async (landscapeId: string) => {
    pollingRef.current = true;
    const POLL_INTERVAL_MS = 3000;
    const MAX_POLLS = 300; // 300 × 3 s = 15 minutes absolute ceiling
    let polls = 0;
    while (true) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      polls++;
      try {
        const current = await actorLandscapeService.getById(landscapeId);
        setLandscape(current);
        if (!RUNNING_STATUSES.includes(current.status)) {
          if (current.error_message) setError(current.error_message);
          setBusy(null);
          pollingRef.current = false;
          return;
        }
      } catch {
        setBusy(null);
        pollingRef.current = false;
        setError(t('pages.actorLandscape.errorPoll'));
        return;
      }
      if (polls >= MAX_POLLS) {
        setBusy(null);
        pollingRef.current = false;
        setError(t('pages.actorLandscape.errorPollTimeout',
          'Pipeline step timed out. The server may have restarted — please reload the page and retry.'));
        return;
      }
    }
  };

  const load = async () => {
    if (!kbId) return;
    try {
      setLoading(true); setError(null);
      const [kbData, sums] = await Promise.all([
        knowledgeBaseService.getById(kbId),
        actorLandscapeService.listForKB(kbId),
      ]);
      setKb(kbData); setSummaries(sums);
      if (sums.length > 0 && !landscape) {
        const full = await actorLandscapeService.getById(sums[0].id);
        setLandscape(full);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('pages.actorLandscape.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kbId || !newName.trim()) return;
    try {
      setCreating(true);
      const created = await actorLandscapeService.create(kbId, { name: newName.trim() });
      setLandscape(created);
      setSummaries(s => [{ id: created.id, kb_id: created.kb_id, name: created.name, status: created.status, actor_count: 0, created_at: created.created_at, last_updated: created.last_updated }, ...s]);
      setCreateDialogOpen(false); setNewName('');
    } catch (e) {
      alert(e instanceof Error ? e.message : t('pages.actorLandscape.errorCreate'));
    } finally {
      setCreating(false);
    }
  };

  const getApiError = (e: unknown): string => {
    if (axios.isAxiosError(e)) return e.response?.data?.detail || e.message;
    return e instanceof Error ? e.message : t('pages.actorLandscape.errorStep');
  };

  const runStep = async (step: 'extract' | 'normalize' | 'classify-companies' | 'map-technology' | 'generate-report' | 'run-pipeline') => {
    if (!landscape) return;
    setBusy(step); setError(null);
    const isAsync = ['normalize', 'generate-report', 'run-pipeline'].includes(step);
    try {
      let updated: ActorLandscape;
      switch (step) {
        case 'extract': updated = await actorLandscapeService.extract(landscape.id); break;
        case 'normalize': updated = await actorLandscapeService.normalize(landscape.id); break;
        case 'classify-companies': updated = await actorLandscapeService.classifyCompanies(landscape.id); break;
        case 'map-technology': updated = await actorLandscapeService.mapTechnology(landscape.id); break;
        case 'generate-report': updated = await actorLandscapeService.generateReport(landscape.id); break;
        case 'run-pipeline': updated = await actorLandscapeService.runPipeline(landscape.id); break;
      }
      setLandscape(updated);
      if (isAsync) {
        // API returned immediately (background task started) — poll until status settles
        pollUntilDone(updated.id);
        // setBusy stays set; polling will clear it
      } else {
        setBusy(null);
      }
    } catch (e) {
      setError(getApiError(e));
      setBusy(null);
    }
  };

  const handleUpdateActor = async (actorId: string, updates: ActorUpdate) => {
    if (!landscape) return;
    const updated = await actorLandscapeService.updateActor(landscape.id, actorId, updates);
    setLandscape(updated);
  };

  const handleExportActors = async () => {
    if (!landscape) return;
    setExporting(true);
    try {
      // The token lives in sessionStorage of THIS tab — use fetch so it is included.
      const token = sessionStorage.getItem('access_token');
      const response = await fetch(actorLandscapeService.exportActorsUrl(landscape.id), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        // Surface the server's own error message for easier diagnosis
        let detail = `Server error ${response.status}`;
        try { const body = await response.json(); detail = body.detail ?? detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      const blob = await response.blob();
      // Trigger the browser Save-As dialog by creating a temporary <a> and clicking it.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `actors_${landscape.name.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 50)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setError(`Export failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  const filteredActors = useMemo(() => {
    if (!landscape) return [];
    let list = landscape.actors;
    if (actorSearch) list = list.filter(a => a.canonical_name.toLowerCase().includes(actorSearch.toLowerCase()));
    if (typeFilter !== 'all') list = list.filter(a => a.actor_type === typeFilter);
    if (sizeFilter !== 'all') list = list.filter(a => (a.size_category ?? 'unknown') === sizeFilter);
    if (zoneFilter !== 'all') list = list.filter(a => countryZone(a.country) === zoneFilter);
    return list;
  }, [landscape, actorSearch, typeFilter, sizeFilter, zoneFilter]);

  const statusIndex = landscape ? STATUS_ORDER.indexOf(landscape.status) : -1;
  const isRunning = landscape ? RUNNING_STATUSES.includes(landscape.status) : false;

  if (loading) return (
    <>
      <PageHeader title={t('pages.actorLandscape.title')} />
      <div className="px-6 py-4 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
    </>
  );

  return (
    <>
      <PageHeader
        title={kb ? `${t('pages.actorLandscape.title')} — ${kb.name}` : t('pages.actorLandscape.title')}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} className="mr-1" />{t('common.back')}
          </Button>
        }
      />

      <div className="px-6 py-4 space-y-4">
        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        {landscape?.error_message && !error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {t('pages.actorLandscape.backgroundError')}: {landscape.error_message}
          </div>
        )}

        {/* Landscape selector + create */}
        <div className="flex items-center gap-2 flex-wrap">
          {summaries.map(s => (
            <button
              key={s.id}
              onClick={async () => { const full = await actorLandscapeService.getById(s.id); setLandscape(full); }}
              className={`flex items-center gap-2 rounded border px-3 py-1.5 text-sm transition-all ${landscape?.id === s.id ? 'border-blue-400 bg-blue-50 text-blue-800' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}
            >
              {s.name}
              <StatusBadge status={s.status} />
              <span className="text-xs text-slate-400">{s.actor_count} {t('pages.actorLandscape.actors')}</span>
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={() => setCreateDialogOpen(true)}>
            <Plus size={13} className="mr-1" />{t('pages.actorLandscape.newLandscape')}
          </Button>
        </div>

        {landscape && (
          <>
            {/* Pipeline controls */}
            <div className="flex items-center gap-2 flex-wrap border border-slate-200 rounded-lg px-4 py-3 bg-slate-50">
              <span className="text-xs font-medium text-slate-500 mr-1">{t('pages.actorLandscape.pipeline')}:</span>

              <Button size="sm" variant="outline" disabled={!!busy || isRunning} onClick={() => runStep('extract')} className="h-7 text-xs">
                {busy === 'extract' ? t('common.running') : '1 · ' + t('pages.actorLandscape.stepExtract')}
              </Button>
              <Button size="sm" variant="outline" disabled={!!busy || isRunning || statusIndex < 1} onClick={() => runStep('normalize')} className="h-7 text-xs">
                {busy === 'normalize' ? t('common.running') : '2 · ' + t('pages.actorLandscape.stepNormalize')}
              </Button>
              <Button size="sm" variant="outline" disabled={!!busy || isRunning || statusIndex < 1} onClick={() => runStep('classify-companies')} className="h-7 text-xs">
                {busy === 'classify-companies' ? t('common.running') : '3 · ' + t('pages.actorLandscape.stepClassifyCompanies', 'Classify companies')}
              </Button>
              <Button size="sm" variant="outline" disabled={!!busy || isRunning || statusIndex < 1} onClick={() => runStep('map-technology')} className="h-7 text-xs">
                {busy === 'map-technology' ? t('common.running') : '4 · ' + t('pages.actorLandscape.stepMapTech')}
              </Button>
              <Button size="sm" variant="outline" disabled={!!busy || isRunning || statusIndex < 1} onClick={() => runStep('generate-report')} className="h-7 text-xs">
                {busy === 'generate-report' ? t('common.running') : '5 · ' + t('pages.actorLandscape.stepReport')}
              </Button>

              <div className="border-l border-slate-300 mx-1 h-5" />

              <Button size="sm" disabled={!!busy || isRunning} onClick={() => runStep('run-pipeline')} className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white">
                <Play size={11} className="mr-1" />{busy === 'run-pipeline' ? t('common.running') : t('pages.actorLandscape.runAll')}
              </Button>

              <div className="border-l border-slate-300 mx-1 h-5" />

              <Button
                size="sm" variant="outline"
                disabled={!landscape.report_markdown}
                onClick={() => openReportWindow(landscape.name, landscape.report_markdown!)}
                className="h-7 text-xs"
                title={landscape.report_markdown ? 'Open report in new tab' : 'No report yet — run step 5 first'}
              >
                <FileText size={12} className="mr-1" />Report
              </Button>

              <Button
                size="sm" variant="outline"
                disabled={landscape.actors.length === 0 || exporting}
                onClick={handleExportActors}
                className="h-7 text-xs"
                title="Export all actors to Excel"
              >
                <Download size={12} className="mr-1" />{exporting ? 'Exporting…' : 'Export'}
              </Button>

              <div className="ml-auto flex items-center gap-2">
                <StatusBadge status={landscape.status} />
                <span className="text-xs text-slate-400">{landscape.actors.length} {t('pages.actorLandscape.actors')}</span>
              </div>
            </div>

            {/* Actors table */}
            {landscape.actors.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    className="border border-slate-200 rounded px-2 py-1 text-xs w-48 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    placeholder={t('pages.actorLandscape.searchActors')}
                    value={actorSearch}
                    onChange={e => setActorSearch(e.target.value)}
                  />
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="h-7 text-xs w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('pages.actorLandscape.allTypes')}</SelectItem>
                      {Object.entries(ACTOR_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={sizeFilter} onValueChange={setSizeFilter}>
                    <SelectTrigger className="h-7 text-xs w-40"><SelectValue placeholder="All sizes" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sizes</SelectItem>
                      {(Object.entries(SIZE_LABELS) as [SizeCategory, string][]).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={zoneFilter} onValueChange={setZoneFilter}>
                    <SelectTrigger className="h-7 text-xs w-36"><SelectValue placeholder="All countries" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All countries</SelectItem>
                      {Object.entries(COUNTRY_ZONE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-slate-400 ml-auto">{filteredActors.length} / {landscape.actors.length}</span>
                </div>

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 text-[11px] uppercase tracking-wide">
                        <TableHead className="w-4 py-2" />
                        <TableHead className="py-2">{t('pages.actorLandscape.colName')}</TableHead>
                        <TableHead className="py-2">{t('pages.actorLandscape.colType')}</TableHead>
                        <TableHead className="py-2 w-14">{t('pages.actorLandscape.colCountry')}</TableHead>
                        <TableHead className="py-2 w-32">Size</TableHead>
                        <TableHead className="py-2 w-24">Zone</TableHead>
                        <TableHead className="py-2">{t('pages.actorLandscape.colGroup')}</TableHead>
                        <TableHead className="py-2 text-center w-16">{t('pages.actorLandscape.colPatents')}</TableHead>
                        <TableHead className="py-2 text-center w-16">{t('pages.actorLandscape.colPubs')}</TableHead>
                        <TableHead className="py-2">{t('pages.actorLandscape.colTechAreas')}</TableHead>
                        <TableHead className="py-2 text-center w-14">{t('pages.actorLandscape.colConfidence')}</TableHead>
                        <TableHead className="py-2 w-14" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredActors.map(actor => (
                        <ActorRow key={actor.actor_id} actor={actor} onSave={handleUpdateActor} />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

          </>
        )}

        {!landscape && summaries.length === 0 && !loading && (
          <div className="text-center py-12 text-slate-400 text-sm">
            <p>{t('pages.actorLandscape.noLandscape')}</p>
            <Button size="sm" className="mt-3" onClick={() => setCreateDialogOpen(true)}>
              <Plus size={13} className="mr-1" />{t('pages.actorLandscape.newLandscape')}
            </Button>
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('pages.actorLandscape.newLandscape')}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">{t('pages.actorLandscape.landscapeName')}</label>
              <Input
                className="mt-1 h-8 text-sm"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder={t('pages.actorLandscape.landscapeNamePlaceholder')}
                required
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" size="sm" onClick={() => setCreateDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" size="sm" disabled={creating || !newName.trim()}>{creating ? t('common.creating') : t('common.create')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
